import { CalendarCheck, Info } from 'lucide-react';
import { ApiError } from '../../api/envelope';
import type { Campaign, CampaignBookings } from '../../api/types';
import { formatTenantMoney } from '../../lib/tenantCurrency';
import { useTimeFormat } from '../../lib/tenantTime';
import type { LoadState } from '../../lib/useLoad';
import { JobLink } from './booking/JobLink';
import { ourRef } from '../../lib/reservationRef';

/** The windows offered — a week is the default every campaign tool reports on. */
const WINDOWS = [3, 7, 14, 30];

/**
 * What the people a campaign reached booked afterwards — read live from dispatch
 * by the address each recipient was sent to (services/campaignBookings.js).
 *
 * Unlike the website card below it, this sees bookings made by phone, by reply,
 * in the app or by an agent — anything with the recipient's dispatch account on
 * it. The wording is "within N days of receiving it" and never "because of it":
 * a regular who books every Friday books on the Friday after the campaign too.
 *
 * `checked: false` renders as "couldn't check", never as zero — a silent zero
 * here is how a working campaign gets cancelled.
 */
export function CampaignBookingsCard({
  campaign,
  days,
  onDays,
  load,
}: {
  campaign: Campaign;
  days: number;
  onDays: (days: number) => void;
  load: LoadState<CampaignBookings | null>;
}) {
  const { fmtDate } = useTimeFormat();
  if (!campaign.startedAt) return null;
  if (load.error) {
    if (load.error instanceof ApiError && load.error.kind === 'forbidden') return null;
    return (
      <div className="cmg-insight">
        <Info size={15} />
        <span>Could not load the reservations that followed this campaign.</span>
      </div>
    );
  }
  const data = load.data;

  return (
    <div className="cmg-card cmg-website" data-card="bookings" style={{ gap: 10 }}>
      <div className="cmg-website-head">
        <CalendarCheck size={14} />
        <span className="cmg-website-title">Reservations that followed</span>
        <div style={{ flex: 1 }} />
        <div className="cmg-filter-chips">
          {WINDOWS.map((window) => (
            <button
              key={window}
              type="button"
              className={`cmg-filter-chip${days === window ? ' on' : ''}`}
              onClick={() => onDays(window)}
            >
              {window} days
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <div className="cmg-hint">Reading reservations from dispatch…</div>
      ) : !data.checked ? (
        <div className="cmg-insight">
          <Info size={15} />
          <span>
            Couldn’t check reservations{data.reason ? ` — ${data.reason}` : ''}. This is not the same as
            none; try again shortly.
          </span>
        </div>
      ) : (
        <>
          <div className="cstat-row">
            <div className="cstat">
              <div className="cstat-label">Reservations</div>
              <div className={`cstat-value${data.bookings > 0 ? ' green' : ''}`}>
                {data.bookings.toLocaleString()}
              </div>
            </div>
            <div className="cstat">
              <div className="cstat-label">People who booked</div>
              <div className="cstat-value">{data.bookers.toLocaleString()}</div>
            </div>
            {data.revenue !== null && (
              <div className="cstat">
                <div className="cstat-label">Reservation value</div>
                <div className="cstat-value">{formatTenantMoney(data.revenue, 0)}</div>
              </div>
            )}
          </div>
          {data.rows.length > 0 && (
            <div className="ctable">
              {data.rows.map((row) => (
                <div key={row.jobId} className="ctable-row">
                  <div style={{ flex: 1.2 }}>{row.recipientName || row.address}</div>
                  <div style={{ flex: 1 }}>
                    {/* Every booking number the console draws is a JobLink, so a press
                        means the same thing here as on the contact panel. */}
                    <JobLink kind="job" id={row.jobId} icon>
                      {ourRef('job', row.uid ?? row.jobId)}
                    </JobLink>
                  </div>
                  <div style={{ flex: 1 }}>Booked {fmtDate(row.createdAt)}</div>
                  {row.total !== null && <div style={{ width: 110 }}>{formatTenantMoney(row.total, 2)}</div>}
                </div>
              ))}
            </div>
          )}
          <div className="cmg-hint">
            Trips the people this campaign reached booked within {data.days} days of receiving it —
            by any route, matched on the address it was sent to. Quotes and cancellations are not
            counted. It shows what followed the campaign, not proof that it caused it.
          </div>
        </>
      )}
    </div>
  );
}
